import React, { JSX, useCallback, useState } from "react";
import { AccessControlKeys } from "utilities/enumerations/authorization/access-control-keys";
import {
    ActiveStatus,
} from "models/enumerations/active-status/active-status";
import { ButtonSize, ButtonStyle } from "components/buttons/button/button";
import { ButtonIcon } from "components/buttons/button-icon/button-icon";
import { CollectionUtils } from "andculturecode-javascript-core";
import { HeaderBanner } from "components/header-banner/header-banner";
import { Icons } from "components/icons/constants/icons";
import { Pager } from "components/pager/pager";
import {
    Paragraph,
    ParagraphSize,
    ParagraphStyle,
} from "components/typography/paragraph/paragraph";
import { ProductList } from "components/products/product-lists/product-list/product-list";
import { ProductModal } from "components/products/product-modal/product-modal";
import { ProductRecord } from "models/view-models/products/product-record";
import { ProductService } from "utilities/services/products/product-service";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { ToastManager } from "utilities/toast/toast-manager";
import { TrainingType } from "models/enumerations/courses/training-type";
import { t } from "utilities/localization/t";
import { useProducts } from "utilities/hooks/models/products/use-products";
import { validatePageAccess } from "utilities/decorators/aspects/authorization/validate-page-access";
import { useHasAccess } from "utilities/hooks/aspects/authorization/use-has-access";
import { useProductSearch } from "utilities/contexts/search/product/product-search-context";
import AdminProductFilterComponent from "../admin-product-filter-component/admin-product-filter-component";
import "./admin-product-list-page.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface AdminProductListPageProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "admin-product-list-page";
const ITEMS_PER_PAGE = 20;

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const AdminProductListPage: React.FC<AdminProductListPageProps> = validatePageAccess(
    AccessControlKeys.AdminProductListPage
)((): JSX.Element => {

    const { initialCriteria, setCriteria } = useProductSearch();
    const [showProductModal, setShowProductModal] = useState(false);
    const { list: listProducts } = ProductService.useList();
    const canModifyProducts = useHasAccess(AccessControlKeys.CanModifyProducts);
    const [associatedProducts, setAssociatedProducts] = useState<ProductRecord[]>([]);

    const { products, rowCount: totalNumberOfProducts } = useProducts({
        activeStatusFilter: initialCriteria?.status ?? [ActiveStatus.Active],
        applySearch: true,
        includeAvailableForAENOnly: !!initialCriteria.availableForAEN,
        includeArchived: true,
        includeCreatedBy: true,
        includeLatestVersion: true,
        includeLastModifiedBy: true,
        language: initialCriteria?.language ?? undefined,
        searchText: initialCriteria?.searchText ?? "",
        skip: ((initialCriteria?.currentPage ?? 1) - 1) * ITEMS_PER_PAGE,
        take: ITEMS_PER_PAGE,
        topic: initialCriteria?.topic ?? undefined,
        trainingType: initialCriteria?.productType ?? undefined,
    });

    const onPageClick = (pageNumber: number) => {
        setCriteria((prev) => ({ ...prev, currentPage: pageNumber }));
    };
    
    const handleModalOpen = () => {
        setShowProductModal(true);
        fetchAssociatedProductList();
    };

    const fetchAssociatedProductList = useCallback(async (): Promise<void> => {
        try {
            const listProductsResponse = await listProducts({
                activeStatusFilter: [ActiveStatus.Active, ActiveStatus.Draft],
                trainingType: TrainingType.InstructorLedTraining,
            });

            if (
                listProductsResponse?.resultObjects == null ||
                listProductsResponse.results == null ||
                listProductsResponse.results.hasErrors()
            ) {
                throw new Error();
            }
            setAssociatedProducts(listProductsResponse.resultObjects);
        } catch {
            ToastManager.error(t("problemLoadingAssociatedProductsList"));
            setAssociatedProducts([]);
        }
    }, [listProducts]);


    return (
        <>
            <HeaderBanner
                dataTestId={`admin-product-list-page:product-list:header`}
                title={t("products")}>
                {canModifyProducts && (
                    <ButtonIcon
                        dataTestId={`admin-product-list-page:add-new-product:btn`}
                        text={t("addNewProduct")}
                        onClick={() => handleModalOpen()}
                        buttonSize={ButtonSize.Medium}
                        buttonStyle={ButtonStyle.TertiaryAlt}
                        iconType={Icons.Plus}
                    />
                )}
            </HeaderBanner>
            <div className={`${CSS_CLASS_NAME}__window`}>
                <div className={`${CSS_CLASS_NAME}__list__view`}>
                    <div className={`${CSS_CLASS_NAME}__window__sidebar`}>
                        <div className={`${CSS_CLASS_NAME}__window__sidebar__filter`}>
                            <Paragraph
                                cssClassName={`${CSS_CLASS_NAME}__window__sidebar__filter__title`}
                                size={ParagraphSize.XLarge}
                                style={ParagraphStyle.Label}>
                                {t("filterResults")}
                            </Paragraph>
                            <div
                                className={`${CSS_CLASS_NAME}__window__sidebar__filter__selections`}>

                                
                            <AdminProductFilterComponent />
                            </div>
                        </div>
                    
                    </div>
                    <div className={`${CSS_CLASS_NAME}__window__main`}>
                        <div className={`${CSS_CLASS_NAME}__window__main__products`}>
                            <div className={`${CSS_CLASS_NAME}__window__main__products__content`}>
                                <SkipNavContent>
                                    <ProductList products={products} />
                                </SkipNavContent>
                            </div>
                        </div>
                        {CollectionUtils.hasValues(products) && (
                            <div className={`${CSS_CLASS_NAME}__window__main__pager`}>
                                <Pager
                                    currentPage={initialCriteria?.currentPage ?? 1}
                                    totalPages={totalNumberOfProducts / ITEMS_PER_PAGE}
                                    onPageClick={onPageClick}
                                    itemsPerPage={ITEMS_PER_PAGE}
                                    totalItems={totalNumberOfProducts}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ProductModal
                associatedProducts={associatedProducts}
                open={showProductModal}
                setOpen={setShowProductModal}
            />
        </>
    );
});

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { AdminProductListPage };

// #endregion Exports
